import React, { useState, useEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import {
  Map,
  APIProvider,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import DashboardPage from "../../layout";
import { createLmdTerritory, updateLmdTerritory, getLmdTerritory, getAllLmdTerritories, deleteLmdTerritory } from "../../api/territoryMap";
import { assignSalesOffices } from "../../pages/admin_panel/api/territory";
import { getGlobalFilter } from "../../api/filters";
import Dropdown from "../../pages/admin_panel/components/Dropdown";
import { CustomModal } from "../../pages/admin_panel/components/CustomModal";
import { toast } from "react-toastify";
import { dd_supervisor, preseller, salesman } from "../../utility/constant";
import infoCircleIcon from "../../assets/Info-Circle.svg";
import InfoCircleWhite from "../../assets/infoCircleWhite.svg";
import PaginationComponent from "../../components/Pagination";

// Helper function to calculate center of polygon
const getPolygonCenter = (coordinates: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral => {
  if (coordinates.length === 0) {
    return { lat: 0, lng: 0 };
  }
  
  let sumLat = 0;
  let sumLng = 0;
  
  coordinates.forEach(coord => {
    sumLat += coord.lat;
    sumLng += coord.lng;
  });
  
  return {
    lat: sumLat / coordinates.length,
    lng: sumLng / coordinates.length
  };
};

// Helper function to get city from coordinates using reverse geocoding
const getCityFromCoordinates = async (
  coordinates: google.maps.LatLngLiteral[]
): Promise<string> => {
  return new Promise((resolve) => {
    if (!coordinates || coordinates.length === 0) {
      resolve("");
      return;
    }

    const center = getPolygonCenter(coordinates);
    const geocoder = new google.maps.Geocoder();
    
    geocoder.geocode(
      { location: center },
      (results, status) => {
        if (status === "OK" && results && results.length > 0) {
          const result = results[0];
          const addressComponents = result.address_components;
          
          const getComponent = (type: string) =>
            addressComponents.find((component) => component.types.includes(type))?.long_name;
          
          // Try different city types (locality, administrative_area_level_2, etc.)
          const city = getComponent("locality") || 
                      getComponent("administrative_area_level_2") ||
                      getComponent("administrative_area_level_1") ||
                      "";
          
          resolve(city || "");
        } else {
          console.warn("Geocode failed for territory center:", status);
          resolve("");
        }
      }
    );
  });
};

interface Territory {
  id: string;
  name: string;
  area: string;
  coordinates: google.maps.LatLngLiteral[];
  color: string;
  apiId?: string | number; // Store the API territory ID
  comment?: string; // Store comment from API
  territory_id?: string | number;
  city?: string;
  country?: string;
  status?: string; // 'active' or 'inactive'
  status_active?: boolean; // Boolean flag for active status
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  sales_offices?: Array<{ sales_office_id: string; sales_office_name: string }>;
}

const saudiCenter = { lat: 24.774265, lng: 46.738586 };
const saudiBounds = {
  north: 33.0,
  south: 16.0,
  east: 60.0,
  west: 34.0,
};

// Helper function to safely convert coordinates to numbers
const parseCoordinate = (value: any): number => {
  if (typeof value === 'number' && isFinite(value)) {
    return value;
  }
  const parsed = parseFloat(value);
  if (isNaN(parsed) || !isFinite(parsed)) {
    console.warn("Invalid coordinate value:", value);
    return 0;
  }
  return parsed;
};

// Helper function to lighten a hex color
const lightenColor = (color: string, percent: number): string => {
  // Remove # if present
  const hex = color.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Lighten by increasing each component towards white (255)
  const newR = Math.min(255, Math.floor(r + (255 - r) * percent));
  const newG = Math.min(255, Math.floor(g + (255 - g) * percent));
  const newB = Math.min(255, Math.floor(b + (255 - b) * percent));
  
  // Convert back to hex
  const toHex = (n: number) => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

// Helper function to convert API coordinate to LatLngLiteral
const convertToLatLngLiteral = (coord: any): google.maps.LatLngLiteral | null => {
  if (!coord || (typeof coord.lat === 'undefined' && typeof coord.lng === 'undefined')) {
    console.warn("Invalid coordinate object:", coord);
    return null;
  }
  
  const lat = parseCoordinate(coord.lat);
  const lng = parseCoordinate(coord.lng);
  
  if (lat === 0 && lng === 0 && (coord.lat !== 0 || coord.lng !== 0)) {
    // This might be a real 0,0 coordinate, but log it to be safe
    console.warn("Coordinate parsed to 0,0 from:", coord);
  }
  
  return { lat, lng };
};

// Helper function to check if a point is inside a polygon using ray casting algorithm
const isPointInPolygon = (point: google.maps.LatLngLiteral, polygon: google.maps.LatLngLiteral[]): boolean => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;
    
    const intersect = ((yi > point.lat) !== (yj > point.lat)) &&
      (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

// Helper function to calculate distance between two points in meters using Haversine formula
const calculateDistance = (
  point1: google.maps.LatLngLiteral,
  point2: google.maps.LatLngLiteral
): number => {
  const R = 6371000; // Earth's radius in meters
  const dLat = ((point2.lat - point1.lat) * Math.PI) / 180;
  const dLng = ((point2.lng - point1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((point1.lat * Math.PI) / 180) *
      Math.cos((point2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Helper function to check minimum distance between consecutive points
const checkMinimumDistance = (
  coordinates: google.maps.LatLngLiteral[],
  minDistanceMeters: number = 10 // Default: 10 meters minimum distance
): { valid: boolean; message?: string; closePoints?: Array<{ index: number; distance: number }> } => {
  if (coordinates.length < 2) {
    return { valid: true };
  }

  const closePoints: Array<{ index: number; distance: number }> = [];

  for (let i = 0; i < coordinates.length; i++) {
    const nextIndex = (i + 1) % coordinates.length; // Wrap around for last point to first
    const distance = calculateDistance(coordinates[i], coordinates[nextIndex]);
    
    if (distance < minDistanceMeters) {
      closePoints.push({ index: i, distance });
    }
  }

  if (closePoints.length > 0) {
    const minFound = Math.min(...closePoints.map(p => p.distance));
    return {
      valid: false,
      message: `Some points are too close together. Minimum distance required: ${minDistanceMeters}m, but found points ${Math.round(minFound * 100) / 100}m apart.`,
      closePoints
    };
  }

  return { valid: true };
};

// Helper function to check if two polygons overlap
const checkPolygonOverlap = (
  newPolygon: google.maps.LatLngLiteral[],
  existingTerritories: Territory[]
): { overlaps: boolean; overlappingTerritory?: Territory } => {
  // Check if any vertex of the new polygon is inside any existing polygon
  for (const territory of existingTerritories) {
    // Check if any point of new polygon is inside existing polygon
    for (const point of newPolygon) {
      if (isPointInPolygon(point, territory.coordinates)) {
        return { overlaps: true, overlappingTerritory: territory };
      }
    }
    
    // Check if any point of existing polygon is inside new polygon
    for (const point of territory.coordinates) {
      if (isPointInPolygon(point, newPolygon)) {
        return { overlaps: true, overlappingTerritory: territory };
      }
    }
  }
  
  return { overlaps: false };
};

const DrawingControls: React.FC<{
  onDrawingModeChange: (mode: google.maps.drawing.OverlayType | null) => void;
  drawingMode: google.maps.drawing.OverlayType | null;
  onSaveTerritory: (name: string, coordinates: google.maps.LatLngLiteral[], comment: string, salesOfficeIds: string[]) => void;
  territories: Territory[];
  onOpenDeleteModal: (id: string) => void;
  onSelectTerritory: (id: string | null) => void;
  selectedTerritory: string | null;
  tempPolygon: google.maps.LatLngLiteral[] | null;
  tempPolygonOverlap: { overlaps: boolean; overlappingTerritory?: Territory } | null;
  onCancelDrawing: () => void;
  editingTerritory: string | null;
  onStartEdit: (id: string | null) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onToggleStatus: (id: string, statusActive: boolean) => void;
  isLoading?: boolean;
  isLoadingTerritories?: boolean;
  showNameInput: boolean;
  setShowNameInput: (value: boolean) => void;
  territoryName: string;
  setTerritoryName: (value: string) => void;
  territoryComment: string;
  setTerritoryComment: (value: string) => void;
  selectedSalesOfficeIds: string[];
  setSelectedSalesOfficeIds: (value: string[]) => void;
  totalCount: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  onPageChange: (page: number) => void;
  selectedSalesOfficeFilter: string;
  setSelectedSalesOfficeFilter: (value: string) => void;
}> = ({
  onDrawingModeChange,
  drawingMode,
  onSaveTerritory,
  territories,
  onOpenDeleteModal,
  onSelectTerritory,
  selectedTerritory,
  tempPolygon,
  tempPolygonOverlap,
  onCancelDrawing,
  editingTerritory,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onToggleStatus,
  isLoading = false,
  isLoadingTerritories = false,
  showNameInput,
  setShowNameInput,
  territoryName,
  setTerritoryName,
  territoryComment,
  setTerritoryComment,
  selectedSalesOfficeIds,
  setSelectedSalesOfficeIds,
  totalCount,
  currentPage,
  setCurrentPage,
  onPageChange,
  selectedSalesOfficeFilter,
  setSelectedSalesOfficeFilter,
}) => {
  const [salesOffices, setSalesOffices] = useState<any[]>([]);
  const [loadingSalesOffices, setLoadingSalesOffices] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [activeInfoPopup, setActiveInfoPopup] = useState<string | null>(null);
  const [itemsPerPage] = useState(20);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const infoPopupRef = useRef<HTMLDivElement | null>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement>>({});

  // Fetch sales offices using global filter API
  useEffect(() => {
    const fetchSalesOffices = async () => {
      try {
        setLoadingSalesOffices(true);
        const data: any = {
          endpoint: "activity-report",
          positions: [preseller, salesman, dd_supervisor],
          regions: ["Western", "Southern", "Central", "Northern", "Eastern"]
        };
        const response = await getGlobalFilter(data);
        
        if (response?.sales_offices) {
          setSalesOffices(response.sales_offices);
        } else {
          setSalesOffices([]);
        }
      } catch (error: any) {
        console.error("Failed to fetch sales offices:", error);
        setSalesOffices([]);
      } finally {
        setLoadingSalesOffices(false);
      }
    };
    fetchSalesOffices();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        const clickedButton = Object.values(buttonRefs.current).find(
          (btn) => btn && btn.contains(event.target as Node)
        );
        if (!clickedButton) {
          setMenuOpen(null);
          setDropdownPosition(null);
        }
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  // Update dropdown position on scroll or resize
  useEffect(() => {
    const handleScrollOrResize = () => {
      if (menuOpen && dropdownPosition) {
        const territoryId = menuOpen;
        const button = buttonRefs.current[territoryId];
        if (button) {
          const rect = button.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 4, // Fixed positioning uses viewport coordinates
            left: rect.right - 128,
          });
        }
      }
    };

    if (menuOpen) {
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
      return () => {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    }
  }, [menuOpen, dropdownPosition]);

  const toggleMenu = (territoryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (menuOpen === territoryId) {
      setMenuOpen(null);
      setDropdownPosition(null);
    } else {
      const button = buttonRefs.current[territoryId];
      if (button) {
        const rect = button.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4, // Fixed positioning uses viewport coordinates
          left: rect.right - 128, // 128 is dropdown width (w-32 = 8rem = 128px)
        });
      }
      setMenuOpen(territoryId);
    }
  };

  useEffect(() => {
    if (tempPolygon) {
      setShowNameInput(true);
    } else {
      // Clear form state immediately when tempPolygon becomes null
      setShowNameInput(false);
      setTerritoryName("");
      setTerritoryComment("");
      setSelectedSalesOfficeIds([]);
    }
  }, [tempPolygon]);
  

  const handleSave = () => {
    if (territoryName.trim() && tempPolygon) {
      onSaveTerritory(territoryName, tempPolygon, territoryComment, selectedSalesOfficeIds);
      setTerritoryName("");
      setTerritoryComment("");
      setSelectedSalesOfficeIds([]);
      setShowNameInput(false);
    }
  };

  const handleCancel = () => {
    setTerritoryName("");
    setTerritoryComment("");
    setSelectedSalesOfficeIds([]);
    setShowNameInput(false);
    onCancelDrawing();
  };

  const handleSalesOfficeChange = (e: { target: { name: string; value: string | string[] } }) => {
    const { value } = e.target;
    const salesOfficeIds = Array.isArray(value) ? value : [value].filter(v => v);
    setSelectedSalesOfficeIds(salesOfficeIds);
  };

  const salesOfficeOptions = salesOffices.map((office: any) => ({
    value: office.id?.toString() || office.sales_office_id?.toString() || "",
    name: office.name + " (" + office.id + ")",
  }));

  // Add "All" option for sales office filter
  const salesOfficeFilterOptions = [
    { value: "", name: "All Sales Offices" },
    ...salesOfficeOptions,
  ];

  const handleSalesOfficeFilterChange = (e: { target: { name: string; value: string | string[] } }) => {
    const { value } = e.target;
    const salesOfficeId = Array.isArray(value) ? value[0] : value;
    setSelectedSalesOfficeFilter(salesOfficeId);
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    onPageChange(1);
  };

  // Filter territories based on search term (client-side filtering for display)
  // Note: Server-side pagination is handled by the parent component
  const filteredTerritories = territories.filter((territory) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const salesOfficeNames = territory.sales_offices?.map(so => so.sales_office_name.toLowerCase()).join(" ") || "";
    const salesOfficeIds = territory.sales_offices?.map(so => so.sales_office_id.toLowerCase()).join(" ") || "";
    const statusText = (territory.status_active ?? (territory.status?.toLowerCase() === "active")) ? "active" : "inactive";
    return (
      territory.name?.toLowerCase().includes(search) ||
      territory.territory_id?.toString().toLowerCase().includes(search) ||
      salesOfficeNames.includes(search) ||
      salesOfficeIds.includes(search) ||
      territory.country?.toLowerCase().includes(search) ||
      territory.status?.toLowerCase().includes(search) ||
      statusText.includes(search)
    );
  });

  // Use territories directly (already paginated from server)
  const paginatedTerritories = filteredTerritories;

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
    onPageChange(1);
  }, [searchTerm]);

  return (
    <div className="w-full h-full bg-white p-4">
      <h2 className="text-lg font-bold mb-4">LMD Territory</h2>
      
      {!showNameInput ? (
        <>
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => {
                const google = (window as any).google;
                const POLYGON_TYPE = google?.maps?.drawing?.OverlayType?.POLYGON || null;
                if (drawingMode === POLYGON_TYPE) {
                  onDrawingModeChange(null);
                } else {
                  onDrawingModeChange(POLYGON_TYPE);
                }
              }}
              className={`px-3 py-1.5 text-sm rounded ${
                drawingMode !== null
                  ? "bg-purple text-white"
                  : "bg-purple text-white hover:bg-purple"
              }`}
            >
              {drawingMode !== null ? "Stop Drawing" : "Draw Territory"}
            </button>
            <div className="flex-1 flex gap-2">
              <div className="flex-1 flex gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search territories..."
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple"
                />
                <div className="flex-1">
                  {loadingSalesOffices ? (
                    <div className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-gray-100 text-gray-500">
                      Loading...
                    </div>
                  ) : (
                    <Dropdown
                      name="sales_office_filter"
                      value={selectedSalesOfficeFilter}
                      onChange={handleSalesOfficeFilterChange}
                      options={salesOfficeFilterOptions}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                      placeholder="Filter by Sales Office"
                      searchable={true}
                      multiple={false}
                      disabled={loadingSalesOffices}
                    />
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  // Search functionality - filtering is handled in the map function
                }}
                className="px-4 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
              >
                Search
              </button>
            </div>
          </div>

          {isLoadingTerritories ? (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Saved Territories</h3>
              <div className="text-center py-4 text-gray-500">Loading territories...</div>
            </div>
          ) : territories.length > 0 ? (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Territories</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-base border-separate border-spacing-0 border border-gray-400">
                  <thead>
                    <tr>
                      <th className="text-left p-2 font-semibold text-sm border border-gray-400">ID</th>
                      <th className="text-left p-2 font-semibold text-sm border border-gray-400">Name</th>
                      <th className="text-left p-2 font-semibold text-sm border border-gray-400">Area</th>
                      <th className="text-left p-2 font-semibold text-sm border border-gray-400">Sales Office</th>
                      <th className="text-left p-2 font-semibold text-sm border border-gray-400">Status</th>
                      <th className="text-left p-2 font-semibold text-sm border border-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTerritories.map((territory) => (
                      <tr
                        key={territory.id}
                        className={`hover:bg-purple-100 ${
                          selectedTerritory === territory.id ? "bg-purple-200" : ""
                        } ${editingTerritory === territory.id ? "" : "cursor-pointer"}`}
                        onClick={() => {
                          if (editingTerritory !== territory.id) {
                            onSelectTerritory(territory.id === selectedTerritory ? null : territory.id);
                          }
                        }}
                      >
                        <td className="p-2 text-sm border border-gray-400">{territory.territory_id || territory.apiId || "-"}</td>
                        <td className="p-2 text-sm font-medium border border-gray-400 relative group">
                          <div className="flex items-center justify-between gap-2">
                            <span>{territory.name}</span>
                            <button
                              onMouseEnter={() => setActiveInfoPopup(territory.id)}
                              onMouseLeave={() => setActiveInfoPopup(null)}
                              className="p-1 flex-shrink-0"
                              aria-label="Info Icon"
                            >
                              <img
                                src={infoCircleIcon}
                                alt="Info Icon"
                                className="h-4 w-4"
                              />
                            </button>
                          </div>
                          {activeInfoPopup === territory.id && (
                            <div
                              ref={infoPopupRef}
                              className="absolute left-0 top-full mt-1 w-64 p-4 bg-white border border-gray-300 rounded-lg shadow-lg z-50"
                              onMouseEnter={() => setActiveInfoPopup(territory.id)}
                              onMouseLeave={() => setActiveInfoPopup(null)}
                            >
                              <div className="absolute left-4 top-[-8px] w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-gray-300"></div>
                              <div className="relative bg-white rounded-lg pt-2">
                                <div className="space-y-2 text-sm">
                                  <div>
                                    <span className="font-semibold text-gray-700">Territory Name:</span>{" "}
                                    <span className="text-gray-800">{territory.name}</span>
                                  </div>
                                  {territory.created_by && (
                                    <div>
                                      <span className="font-semibold text-gray-700">Created by:</span>{" "}
                                      <span className="text-gray-800">{territory.created_by}</span>
                                    </div>
                                  )}
                                  {territory.created_at && (
                                    <div>
                                      <span className="font-semibold text-gray-700">Created at:</span>{" "}
                                      <span className="text-gray-800">
                                        {new Date(territory.created_at).toLocaleDateString()} {new Date(territory.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                      </span>
                                    </div>
                                  )}
                                  {territory.updated_at && (
                                    <div>
                                      <span className="font-semibold text-gray-700">Updated at:</span>{" "}
                                      <span className="text-gray-800">
                                        {new Date(territory.updated_at).toLocaleDateString()} {new Date(territory.updated_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                      </span>
                                    </div>
                                  )}
                                  {!territory.created_by && !territory.created_at && !territory.updated_at && (
                                    <div className="text-gray-500 text-xs">No additional details available</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-sm border border-gray-400">
                          {territory.area 
                            ? `${(parseFloat(territory.area) / 1000000).toFixed(2)} sq km` 
                            : "-"}
                        </td>
                        <td className="p-2 text-sm border border-gray-400">
                          {territory.sales_offices && territory.sales_offices.length > 0
                            ? territory.sales_offices.map(so => `${so.sales_office_name} (${so.sales_office_id})`).join(", ")
                            : "-"}
                        </td>
                        <td className="p-2 border border-gray-400">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentStatus = territory.status_active ?? (territory.status?.toLowerCase() === "active");
                              onToggleStatus(territory.id, !currentStatus);
                            }}
                            disabled={isLoading || editingTerritory === territory.id}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                              territory.status_active ?? (territory.status?.toLowerCase() === "active")
                                ? "bg-green-500"
                                : "bg-gray-300"
                            }`}
                            title={territory.status_active ?? (territory.status?.toLowerCase() === "active") ? "Active" : "Inactive"}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                territory.status_active ?? (territory.status?.toLowerCase() === "active")
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </button>
                          <span className="ml-2 text-sm text-gray-700">
                            {territory.status_active ?? (territory.status?.toLowerCase() === "active") ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="p-2 relative border border-gray-400">
                          {editingTerritory === territory.id ? (
                            <div className="flex flex-col gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSaveEdit();
                                }}
                                disabled={isLoading}
                                className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                title="Save Changes"
                              >
                                {isLoading ? "Saving..." : "Save"}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelEdit();
                                }}
                                disabled={isLoading}
                                className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                title="Cancel"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="relative">
                              <button
                                ref={(el) => {
                                  if (el) buttonRefs.current[territory.id] = el;
                                }}
                                onClick={(e) => toggleMenu(territory.id, e)}
                                className="text-gray-500 hover:text-gray-800 p-1"
                                title="Actions"
                              >
                                <span className="text-lg">⋮</span>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalCount > itemsPerPage && (
                <div className="mt-4 pt-2 pb-2 border-t">
                  <PaginationComponent
                    totalCount={totalCount}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    setCurrentPage={(page: number) => {
                      setCurrentPage(page);
                      onPageChange(page);
                    }}
                  />
                </div>
              )}
              {/* Dropdown menu rendered outside scrolling container */}
              {menuOpen && dropdownPosition && (
                <div
                  ref={dropdownRef}
                  className="fixed w-32 bg-white border border-gray-200 shadow-lg rounded z-50 text-left"
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                  }}
                >
                  <ul className="py-1 text-sm text-gray-900">
                    <li
                      className="px-4 py-2 hover:bg-gray-200 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        const territoryId = menuOpen;
                        if (territoryId) {
                          onStartEdit(territoryId);
                          setMenuOpen(null);
                          setDropdownPosition(null);
                        }
                      }}
                    >
                      Edit
                    </li>
                    <li
                      className="px-4 py-2 hover:bg-gray-200 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        const territoryId = menuOpen;
                        if (territoryId) {
                          onOpenDeleteModal(territoryId);
                          setMenuOpen(null);
                          setDropdownPosition(null);
                        }
                      }}
                    >
                      Delete
                    </li>
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Territories</h3>
              <div className="text-center py-4 text-gray-500 text-sm">No territories saved yet</div>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3">
          {tempPolygonOverlap?.overlaps && (
            <div className="p-3 bg-red-50 border border-red-300 rounded text-red-700 text-sm">
              <div className="font-semibold mb-1">⚠️ Overlap Detected!</div>
              <div>
                This territory overlaps with "{tempPolygonOverlap.overlappingTerritory?.name || "another territory"}".
                Please adjust the boundaries before saving.
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Territory Name</label>
            <input
              type="text"
              value={territoryName}
              onChange={(e) => setTerritoryName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.ctrlKey) {
                  handleSave();
                } else if (e.key === "Escape") {
                  handleCancel();
                }
              }}
              placeholder="Enter territory name"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Sales Office(s)*</label>
            {loadingSalesOffices ? (
              <div className="flex items-center gap-2 p-2">
                <span className="text-sm text-gray-600">Loading sales offices...</span>
              </div>
            ) : (
              <Dropdown
                name="sales_office_ids"
                value={selectedSalesOfficeIds}
                onChange={handleSalesOfficeChange}
                options={salesOfficeOptions}
                className="w-full border px-3 py-2 rounded"
                placeholder="Select Sales Office(s)"
                searchable={true}
                multiple={true}
                disabled={loadingSalesOffices}
              />
            )}
            {selectedSalesOfficeIds.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedSalesOfficeIds.length} sales office(s) selected
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={!territoryName.trim() || isLoading || (tempPolygonOverlap?.overlaps === true)}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isLoading ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MapDrawingManager = React.forwardRef<
  { cancelDrawing: () => void },
  {
    drawingMode: google.maps.drawing.OverlayType | null;
    onPolygonComplete: (coordinates: google.maps.LatLngLiteral[]) => void;
  }
>(({ drawingMode, onPolygonComplete }, ref) => {
  const map = useMap();
  const drawingLibrary = useMapsLibrary("drawing");
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const polygonCompleteCallbackRef = useRef(onPolygonComplete);

  // Expose cancel method to parent via ref
  React.useImperativeHandle(ref, () => ({
    cancelDrawing: () => {
      if (drawingManagerRef.current) {
        drawingManagerRef.current.setDrawingMode(null);
      }
    },
  }));

  useEffect(() => {
    polygonCompleteCallbackRef.current = onPolygonComplete;
  }, [onPolygonComplete]);

  useEffect(() => {
    if (!map || !drawingLibrary) return;

    if (!drawingManagerRef.current) {
      const DrawingManager = drawingLibrary.DrawingManager;
      const drawingManager = new DrawingManager({
        map: map,
        drawingMode: null,
        drawingControl: false,
        polygonOptions: {
          fillColor: "#4285F4",
          fillOpacity: 0.35,
          strokeWeight: 4,
          strokeColor: "#4285F4",
          clickable: false,
          editable: false,
          zIndex: 1,
        },
      });

      drawingManager.addListener("polygoncomplete", (polygon: google.maps.Polygon) => {
        const path = polygon.getPath();
        const coordinates: google.maps.LatLngLiteral[] = [];
        path.forEach((latLng) => {
          coordinates.push({ lat: latLng.lat(), lng: latLng.lng() });
        });
        polygon.setMap(null); // Remove the temporary polygon from the map
        polygonCompleteCallbackRef.current(coordinates);
      });

      drawingManagerRef.current = drawingManager;
    }

    return () => {
      if (drawingManagerRef.current) {
        const google = (window as any).google;
        if (google?.maps?.event) {
          google.maps.event.clearInstanceListeners(drawingManagerRef.current);
        }
        drawingManagerRef.current.setMap(null);
        drawingManagerRef.current = null;
      }
    };
  }, [map, drawingLibrary]);

  useEffect(() => {
    if (drawingManagerRef.current) {
      if (drawingMode !== null) {
        drawingManagerRef.current.setDrawingMode(drawingMode);
      } else {
        // When canceling, explicitly set drawing mode to null
        // This will cancel any active/incomplete drawing
        drawingManagerRef.current.setDrawingMode(null);
      }
    }
  }, [drawingMode]);

  return null;
});

const MapClickHandler: React.FC<{
  onMapClick: () => void;
}> = ({ onMapClick }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    // Listen for clicks on the map (empty space, not on polygons)
    const clickListener = map.addListener("click", () => {
      onMapClick();
    });

    return () => {
      google.maps.event.removeListener(clickListener);
    };
  }, [map, onMapClick]);

  return null;
};

const TerritoryPolygons: React.FC<{
  territories: Territory[];
  selectedTerritory: string | null;
  tempPolygon: google.maps.LatLngLiteral[] | null;
  tempPolygonOverlap: { overlaps: boolean; overlappingTerritory?: Territory } | null;
  onTerritoryClick: (id: string) => void;
  editingTerritory: string | null;
  onTerritoryUpdate: (id: string, coordinates: google.maps.LatLngLiteral[]) => void;
  editingCoordinates: Record<string, google.maps.LatLngLiteral[]>;
}> = ({ territories, selectedTerritory, tempPolygon, tempPolygonOverlap, onTerritoryClick, editingTerritory, onTerritoryUpdate, editingCoordinates }) => {
  const map = useMap();
  const polygonsRef = useRef<Record<string, google.maps.Polygon>>({});
  const tempPolygonRef = useRef<google.maps.Polygon | null>(null);

  useEffect(() => {
    if (!map) return;

    // Wait for google.maps to be available
    const initPolygons = () => {
      const google = (window as any).google;
      if (!google?.maps?.Polygon) {
        setTimeout(initPolygons, 100);
        return;
      }

      console.log("TerritoryPolygons: Rendering", territories.length, "territories");
      console.log("TerritoryPolygons: editingTerritory =", editingTerritory);
      console.log("TerritoryPolygons: territories =", territories.map(t => ({ id: t.id, name: t.name, coordsLength: t.coordinates.length })));

      // Clear existing polygons and listeners
      Object.values(polygonsRef.current).forEach((polygon) => {
        // Remove path listeners if they exist
        const listeners = (polygon as any).__updateListeners;
        if (listeners) {
          listeners.forEach((listener: google.maps.MapsEventListener) => {
            google.maps.event.removeListener(listener);
          });
        }
        polygon.setMap(null);
      });
      polygonsRef.current = {};

      // Create polygons for each territory
      territories.forEach((territory) => {
        // Validate coordinates
        if (!territory.coordinates || !Array.isArray(territory.coordinates) || territory.coordinates.length === 0) {
          console.warn("Territory has invalid coordinates:", territory);
          return;
        }

        const isEditing = editingTerritory === territory.id;
        // Use editingCoordinates if available and territory is being edited, otherwise use territory coordinates
        let coordinates = isEditing && editingCoordinates[territory.id] && Array.isArray(editingCoordinates[territory.id])
          ? editingCoordinates[territory.id] 
          : territory.coordinates;

        // Validate and sanitize coordinates - ensure all are valid numbers
        coordinates = coordinates
          .map((coord: google.maps.LatLngLiteral) => ({
            lat: parseCoordinate(coord.lat),
            lng: parseCoordinate(coord.lng),
          }))
          .filter((coord: google.maps.LatLngLiteral) => 
            isFinite(coord.lat) && isFinite(coord.lng)
          );

        if (coordinates.length < 3) {
          console.warn(`Territory ${territory.name} has insufficient valid coordinates: ${coordinates.length}`);
          return; // Skip polygon creation if not enough valid coordinates
        }

        console.log(`Creating polygon for ${territory.name}: ${coordinates.length} coordinates`);

        // Check if territory is inactive
        const isInactive = !(territory.status_active ?? (territory.status?.toLowerCase() === "active"));
        // Use grey color for inactive territories, otherwise use territory color
        const territoryColor = isInactive ? "#808080" : territory.color;
        
        const polygon = new google.maps.Polygon({
          paths: coordinates,
          fillColor: territoryColor,
          fillOpacity: selectedTerritory === territory.id ? 0.5 : 0.3,
          strokeWeight: selectedTerritory === territory.id ? 5 : 4,
          strokeColor: selectedTerritory === territory.id ? lightenColor(territoryColor, 0.2) : territoryColor,
          clickable: !isEditing,
          editable: isEditing,
          draggable: false,
          zIndex: selectedTerritory === territory.id ? 10 : 1,
          map: map,
        });

        polygon.addListener("click", (e: google.maps.MapMouseEvent) => {
          if (!isEditing) {
            e.stop(); // Stop event propagation to prevent map click handler from firing
            onTerritoryClick(territory.id);
          }
        });

        // Listen for path changes when editing
        if (isEditing) {
          const path = polygon.getPath();
          const updateListener = path.addListener("set_at", () => {
            const coordinates: google.maps.LatLngLiteral[] = [];
            path.forEach((latLng: google.maps.LatLng) => {
              coordinates.push({ lat: latLng.lat(), lng: latLng.lng() });
            });
            onTerritoryUpdate(territory.id, coordinates);
          });
          
          const insertListener = path.addListener("insert_at", () => {
            const coordinates: google.maps.LatLngLiteral[] = [];
            path.forEach((latLng: google.maps.LatLng) => {
              coordinates.push({ lat: latLng.lat(), lng: latLng.lng() });
            });
            onTerritoryUpdate(territory.id, coordinates);
          });

          const removeListener = path.addListener("remove_at", () => {
            const coordinates: google.maps.LatLngLiteral[] = [];
            path.forEach((latLng: google.maps.LatLng) => {
              coordinates.push({ lat: latLng.lat(), lng: latLng.lng() });
            });
            onTerritoryUpdate(territory.id, coordinates);
          });

          // Store listeners for cleanup
          (polygon as any).__updateListeners = [updateListener, insertListener, removeListener];
        }

        polygonsRef.current[territory.id] = polygon;
      });
    };

    initPolygons();

    return () => {
      Object.values(polygonsRef.current).forEach((polygon) => {
        const listeners = (polygon as any).__updateListeners;
        if (listeners) {
          listeners.forEach((listener: google.maps.MapsEventListener) => {
            google.maps.event.removeListener(listener);
          });
        }
        polygon.setMap(null);
      });
      polygonsRef.current = {};
    };

    initPolygons();
  }, [map, territories, selectedTerritory, editingTerritory, editingCoordinates, onTerritoryClick, onTerritoryUpdate]);

  useEffect(() => {
    if (!map) return;

    // Handle temp polygon
    const initTempPolygon = () => {
      const google = (window as any).google;
      if (!google?.maps?.Polygon) {
        if (tempPolygon) {
          setTimeout(initTempPolygon, 100);
        }
        return;
      }

      if (tempPolygon) {
        if (tempPolygonRef.current) {
          tempPolygonRef.current.setMap(null);
        }
        // Change color to red if overlapping, blue otherwise
        const isOverlapping = tempPolygonOverlap?.overlaps === true;
        const fillColor = isOverlapping ? "#EA4335" : "#4285F4";
        const strokeColor = isOverlapping ? "#EA4335" : "#4285F4";
        tempPolygonRef.current = new google.maps.Polygon({
          paths: tempPolygon,
          fillColor: fillColor,
          fillOpacity: 0.35,
          strokeWeight: isOverlapping ? 5 : 4,
          strokeColor: strokeColor,
          clickable: false,
          zIndex: 5,
          map: map,
        });
      } else {
        if (tempPolygonRef.current) {
          tempPolygonRef.current.setMap(null);
          tempPolygonRef.current = null;
        }
      }
    };

    initTempPolygon();

    return () => {
      if (tempPolygonRef.current) {
        tempPolygonRef.current.setMap(null);
        tempPolygonRef.current = null;
      }
    };
  }, [map, tempPolygon, tempPolygonOverlap]);

  return null;
};

const TerritoryMapView: React.FC = () => {
  const [drawingMode, setDrawingMode] = useState<google.maps.drawing.OverlayType | null>(null);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [selectedTerritory, setSelectedTerritory] = useState<string | null>(null);
  const [tempPolygon, setTempPolygon] = useState<google.maps.LatLngLiteral[] | null>(null);
  const [tempPolygonOverlap, setTempPolygonOverlap] = useState<{ overlaps: boolean; overlappingTerritory?: Territory } | null>(null);
  const [editingTerritory, setEditingTerritory] = useState<string | null>(null);
  const [editingCoordinates, setEditingCoordinates] = useState<Record<string, google.maps.LatLngLiteral[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTerritories, setIsLoadingTerritories] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [territoryToDelete, setTerritoryToDelete] = useState<string | null>(null);
  const nextIdRef = useRef(1);
  const [isCancelling, setIsCancelling] = useState(false);
  const drawingManagerCancelRef = useRef<{ cancelDrawing: () => void } | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 20;
  
  // Sales office filter state
  const [selectedSalesOfficeFilter, setSelectedSalesOfficeFilter] = useState<string>("");
  
  // Form state - lifted to parent so we can clear it directly from handleCancelDrawing
  const [showNameInput, setShowNameInput] = useState(false);
  const [territoryName, setTerritoryName] = useState("");
  const [territoryComment, setTerritoryComment] = useState("");
  const [selectedSalesOfficeIds, setSelectedSalesOfficeIds] = useState<string[]>([]);

  // Check for overlap in real-time when tempPolygon changes
  useEffect(() => {
    if (tempPolygon && tempPolygon.length >= 3) {
      const overlapCheck = checkPolygonOverlap(tempPolygon, territories);
      setTempPolygonOverlap(overlapCheck);
      
      if (overlapCheck.overlaps) {
        const territoryName = overlapCheck.overlappingTerritory?.name || "another territory";
        toast.warning(`⚠️ Territory overlaps with "${territoryName}"! Please adjust the boundaries.`, {
          autoClose: 5000,
        });
      }
    } else {
      setTempPolygonOverlap(null);
    }
  }, [tempPolygon, territories]);

  // Helper function to format territories from API response
  const formatTerritories = useCallback((apiTerritories: any[]): Territory[] => {
    const colors = [
      "#4285F4",
      "#EA4335",
      "#FBBC05",
      "#34A853",
      "#9C27B0",
      "#FF9800",
      "#00BCD4",
      "#E91E63",
    ];

    return apiTerritories
      .filter((apiTerritory) => {
        // Filter out invalid territories
        if (!apiTerritory || !apiTerritory.coordinates || !Array.isArray(apiTerritory.coordinates)) {
          console.warn("Invalid territory data:", apiTerritory);
          return false;
        }
        return true;
      })
      .map((apiTerritory, index) => {
        // Convert API coordinates to google.maps.LatLngLiteral format
        const coordinates = apiTerritory.coordinates
          .map((coord: any) => convertToLatLngLiteral(coord))
          .filter((coord: google.maps.LatLngLiteral | null): coord is google.maps.LatLngLiteral => coord !== null);

        return {
          id: `territory-${apiTerritory.id || index}`,
          name: apiTerritory.territory_name || `Territory ${index + 1}`,
          coordinates,
          color: colors[index % colors.length],
          apiId: apiTerritory.id,
          comment: apiTerritory.comment || "",
          area: apiTerritory.area || "",
          territory_id: apiTerritory.territory_id || apiTerritory.id,
          city: apiTerritory.city || "",
          country: apiTerritory.country || "",
          status: apiTerritory.status || "",
          status_active: apiTerritory.status_active ?? (apiTerritory.status?.toLowerCase() === "active"),
          created_by: apiTerritory.created_by || apiTerritory.created_by_name || "",
          created_at: apiTerritory.created_at || apiTerritory.created_date || "",
          updated_at: apiTerritory.updated_at || apiTerritory.updated_date || "",
          sales_offices: apiTerritory.sales_offices || [],
        };
      });
  }, []);

  // Load territories from API with pagination
  const fetchTerritories = useCallback(async (page: number = 1, salesOfficeId?: string) => {
    setIsLoadingTerritories(true);
    try {
      const response = await getAllLmdTerritories(salesOfficeId || undefined, undefined, page, itemsPerPage);
      
      console.log("API Territories received:", response);
      
      let apiTerritories: any[] = [];
      let total = 0;
      
      // Check if response is paginated
      if (response && typeof response === 'object' && 'territories' in response) {
        const paginatedResponse = response as { territories: any[]; total: number; page: number; size: number };
        apiTerritories = paginatedResponse.territories || [];
        total = paginatedResponse.total || 0;
        setTotalCount(total);
      } else if (Array.isArray(response)) {
        // Legacy: direct array response
        apiTerritories = response;
        setTotalCount(response.length);
      } else {
        console.warn("API returned invalid data structure:", response);
        setTerritories([]);
        setTotalCount(0);
        setIsLoadingTerritories(false);
        return;
      }

      // Check if array is empty
      if (apiTerritories.length === 0) {
        console.log("No territories found in API response");
        setTerritories([]);
        setIsLoadingTerritories(false);
        return;
      }

      // Convert API response to Territory format
      const formattedTerritories = formatTerritories(apiTerritories);

      console.log("Formatted territories:", formattedTerritories);
      setTerritories(formattedTerritories);
      
      // Update nextIdRef
      if (formattedTerritories.length > 0) {
        const maxId = Math.max(...formattedTerritories.map((t: Territory) => {
          const match = t.id.match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        }));
        nextIdRef.current = maxId + 1;
      }
    } catch (error: any) {
      console.error("Error fetching territories:", error);
      toast.error(error?.response?.data?.message || "Failed to load territories");
      setTerritories([]);
      setTotalCount(0);
    } finally {
      setIsLoadingTerritories(false);
    }
  }, [itemsPerPage]);

  // Load territories on mount and when page or sales office filter changes
  useEffect(() => {
    fetchTerritories(currentPage, selectedSalesOfficeFilter || undefined);
  }, [currentPage, selectedSalesOfficeFilter, fetchTerritories]);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // fetchTerritories will be called by the useEffect above
  }, []);

  const handlePolygonComplete = useCallback((coordinates: google.maps.LatLngLiteral[]) => {
    setTempPolygon(coordinates);
    setDrawingMode(null);
  }, []);

  const handleSaveTerritory = useCallback(async (name: string, coordinates: google.maps.LatLngLiteral[], comment: string, salesOfficeIds: string[] = []) => {
    // Validate coordinates first
    if (!coordinates || coordinates.length < 3) {
      toast.error("A territory must have at least 3 coordinates");
      return;
    }

    // Check minimum distance between consecutive points (default: 10 meters)
    const MIN_DISTANCE_METERS = 1;
    const distanceCheck = checkMinimumDistance(coordinates, MIN_DISTANCE_METERS);
    if (!distanceCheck.valid) {
      toast.error(distanceCheck.message || "Some points are too close together. Please ensure consecutive points are at least 10 meters apart.");
      return;
    }

    // Check for overlap with existing territories
    const overlapCheck = checkPolygonOverlap(coordinates, territories);
    if (overlapCheck.overlaps) {
      const territoryName = overlapCheck.overlappingTerritory?.name || "another territory";
      toast.error(`Cannot create territory: The new territory overlaps with "${territoryName}". Please draw a territory that does not overlap with existing territories.`);
      return;
    }

    setIsLoading(true);
    try {
      // Convert coordinates to API format
      const apiCoordinates = coordinates.map(coord => ({
        lat: coord.lat,
        lng: coord.lng,
      }));

      // Call API to create territory
      const createdTerritory = await createLmdTerritory({
        territory_name: name,
        coordinates: apiCoordinates,
        comment: comment || "",
      });

      // Assign sales offices if provided
      if (salesOfficeIds.length > 0 && createdTerritory) {
        try {
          const territoryId = createdTerritory.territory_id;
          if (territoryId) {
            await assignSalesOffices({
              territory_id: territoryId.toString(),
              sales_office_ids: salesOfficeIds,
            });
            toast.success("Territory created and sales offices assigned successfully!");
          } else {
            console.warn("Territory created but territory_id not found in response, cannot assign sales offices");
            toast.warning("Territory created but territory_id not found, sales offices could not be assigned");
          }
        } catch (assignError: any) {
          console.error("Error assigning sales offices:", assignError);
          toast.warning("Territory created but failed to assign sales offices: " + (assignError?.response?.data?.message || "Unknown error"));
        }
      }

      // Refresh territories list from API to ensure sync
      await fetchTerritories(currentPage, selectedSalesOfficeFilter || undefined);
      setTempPolygon(null);
      // Clear editing state after territories are set to prevent render issues
      setEditingTerritory(null);
      setEditingCoordinates({});
      // Only show success message if sales offices weren't assigned (they have their own message)
      if (salesOfficeIds.length === 0) {
        toast.success("Territory created successfully!");
      }
    } catch (error: any) {
      console.error("Error creating territory:", error);
      toast.error(error?.response?.data?.message || "Failed to create territory");
    } finally {
      setIsLoading(false);
    }
  }, [territories, currentPage, selectedSalesOfficeFilter, fetchTerritories]);

  const handleOpenDeleteModal = useCallback((id: string) => {
    setTerritoryToDelete(id);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setTerritoryToDelete(null);
  }, []);

  const handleDeleteTerritory = useCallback(async () => {
    if (!territoryToDelete) {
      toast.error("No territory selected for deletion");
      return;
    }

    const territory = territories.find(t => t.id === territoryToDelete);
    if (!territory) {
      toast.error("Territory not found");
      setIsDeleteModalOpen(false);
      setTerritoryToDelete(null);
      return;
    }

    // If territory has a territory_id, delete via API
    if (territory.territory_id) {
      try {
        await deleteLmdTerritory(territory.territory_id);
        
        // Refresh territories list from API to ensure sync
        await fetchTerritories(currentPage, selectedSalesOfficeFilter || undefined);
        
        if (selectedTerritory === territoryToDelete) {
          setSelectedTerritory(null);
        }
        
        setIsDeleteModalOpen(false);
        setTerritoryToDelete(null);
        toast.success("Territory deleted successfully!");
      } catch (error: any) {
        console.error("Error deleting territory:", error);
        toast.error(error?.response?.data?.message || "Failed to delete territory");
        setIsDeleteModalOpen(false);
        setTerritoryToDelete(null);
      }
    } else {
      // If no API ID, just remove locally
      setTerritories((prev) => prev.filter((t) => t.id !== territoryToDelete));
      if (selectedTerritory === territoryToDelete) {
        setSelectedTerritory(null);
      }
      setIsDeleteModalOpen(false);
      setTerritoryToDelete(null);
    }
  }, [selectedTerritory, territories, territoryToDelete, fetchTerritories, currentPage, selectedSalesOfficeFilter]);

  const handleCancelDrawing = useCallback(() => {
    // FIRST: Stop the DrawingManager immediately (synchronous, no React state)
    if (drawingManagerCancelRef.current) {
      drawingManagerCancelRef.current.cancelDrawing();
    }
    
    // THEN: Clear EVERYTHING in one flushSync - all state is now in TerritoryMapView
    flushSync(() => {
      // Clear form state
      setShowNameInput(false);
      setTerritoryName("");
      setTerritoryComment("");
      setSelectedSalesOfficeIds([]);
      
      // Clear map state
      setTempPolygon(null);
      setTempPolygonOverlap(null);
      setDrawingMode(null);
    });
    
    // Reset cancelling flag after a brief moment
    setTimeout(() => {
      setIsCancelling(false);
    }, 50);
  }, [setShowNameInput, setTerritoryName, setTerritoryComment, setSelectedSalesOfficeIds]);

  const handleStartEdit = useCallback((id: string | null) => {
    if (id) {
      const territory = territories.find(t => t.id === id);
      if (territory) {
        setEditingCoordinates({ [id]: [...territory.coordinates] });
        setEditingTerritory(id);
        setDrawingMode(null);
      }
    }
  }, [territories]);

  const handleSaveEdit = useCallback(async () => {
    if (editingTerritory && editingCoordinates[editingTerritory]) {
      const editedCoordinates = editingCoordinates[editingTerritory];
      
      // Validate coordinates
      if (!editedCoordinates || editedCoordinates.length < 3) {
        toast.error("A territory must have at least 3 coordinates");
        return;
      }

      // Check minimum distance between consecutive points (default: 10 meters)
      const MIN_DISTANCE_METERS = 1;
      const distanceCheck = checkMinimumDistance(editedCoordinates, MIN_DISTANCE_METERS);
      if (!distanceCheck.valid) {
        toast.error(distanceCheck.message || "Some points are too close together. Please ensure consecutive points are at least 10 meters apart.");
        return;
      }

      // Check for overlap with other territories (excluding the current one being edited)
      const otherTerritories = territories.filter(t => t.id !== editingTerritory);
      const overlapCheck = checkPolygonOverlap(editedCoordinates, otherTerritories);
      if (overlapCheck.overlaps) {
        const territoryName = overlapCheck.overlappingTerritory?.name || "another territory";
        toast.error(`Cannot update territory: The edited territory overlaps with "${territoryName}". Please modify the territory so it does not overlap with other territories.`);
        return;
      }

      setIsLoading(true);
      try {
        const territory = territories.find(t => t.id === editingTerritory);
        if (!territory) {
          toast.error("Territory not found");
          setIsLoading(false);
          return;
        }

        // If territory has an API ID, update via API
        if (territory.apiId) {
          const apiCoordinates = editingCoordinates[editingTerritory].map(coord => ({
            lat: coord.lat,
            lng: coord.lng,
          }));

          const response = await updateLmdTerritory(territory.territory_id || territory.apiId, {
            territory_name: territory.name,
            coordinates: apiCoordinates,
            comment: territory.comment || "",
          });

          // Refresh territories list from API to ensure sync
          try {
            await fetchTerritories(currentPage, selectedSalesOfficeFilter || undefined);
            // Clear editing state after territories are set to prevent render issues
            setEditingTerritory(null);
            setEditingCoordinates({});
            toast.success("Territory updated successfully!");
          } catch (refreshError) {
            // If refresh fails, update locally
            const currentEditingId = editingTerritory;
            const validCoordinates = response.coordinates
              ? response.coordinates
                  .map((coord: any) => convertToLatLngLiteral(coord))
                  .filter((coord: google.maps.LatLngLiteral | null): coord is google.maps.LatLngLiteral => coord !== null)
              : [];
            
            setTerritories((prev) =>
              prev.map((t) =>
                t.id === currentEditingId
                  ? { 
                      ...t, 
                      coordinates: validCoordinates.length > 0 ? validCoordinates : t.coordinates,
                      comment: response.comment || t.comment,
                    }
                  : t
              )
            );
            // Clear editing state after territories are set
            setEditingTerritory(null);
            setEditingCoordinates({});
            toast.success("Territory updated successfully!");
            console.error("Error refreshing territories:", refreshError);
          }
        } else {
          // If no API ID, just update locally
          // Store values before clearing editing state
          const currentEditingId = editingTerritory;
          const currentEditingCoords = editingCoordinates[editingTerritory];
          
          setTerritories((prev) =>
            prev.map((t) =>
              t.id === currentEditingId
                ? { ...t, coordinates: currentEditingCoords }
                : t
            )
          );
          // Clear editing state after territories are set to prevent render issues
          setEditingTerritory(null);
          setEditingCoordinates({});
        }
      } catch (error: any) {
        console.error("Error updating territory:", error);
        toast.error(error?.response?.data?.message || "Failed to update territory");
      } finally {
        setIsLoading(false);
      }
    }
  }, [editingTerritory, editingCoordinates, territories, fetchTerritories, currentPage]);

  const handleCancelEdit = useCallback(() => {
    setEditingTerritory(null);
    setEditingCoordinates({});
  }, []);

  const handleTerritoryUpdate = useCallback((id: string, coordinates: google.maps.LatLngLiteral[]) => {
    setEditingCoordinates((prev) => ({
      ...prev,
      [id]: coordinates,
    }));
  }, []);

  const handleToggleStatus = useCallback(async (id: string, statusActive: boolean) => {
    const territory = territories.find(t => t.id === id);
    if (!territory || !territory.apiId) {
      toast.error("Territory not found or cannot be updated");
      return;
    }

    setIsLoading(true);
    try {
      // Call update API with status_active flag
      await updateLmdTerritory(territory.territory_id || territory.apiId, {
        territory_name: territory.name,
        coordinates: territory.coordinates.map(coord => ({
          lat: coord.lat,
          lng: coord.lng,
        })),
        comment: territory.comment || "",
        status_active: statusActive,
      });

      // Refresh territories list from API to ensure sync
      await fetchTerritories(currentPage, selectedSalesOfficeFilter || undefined);
      toast.success(`Territory status updated to ${statusActive ? "active" : "inactive"}`);
    } catch (error: any) {
      console.error("Error updating territory status:", error);
      toast.error(error?.response?.data?.message || "Failed to update territory status");
    } finally {
      setIsLoading(false);
    }
  }, [territories, currentPage, selectedSalesOfficeFilter, fetchTerritories]);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  return (
    <DashboardPage>
      <CustomModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteTerritory}
      />
      <div className="flex flex-col h-[calc(100vh-54px)] overflow-hidden">
        <div className="flex-1 flex flex-row overflow-hidden">
          {/* Sidebar */}
          <div
            className={`overflow-hidden transition-all duration-500 ease-in-out ${
              isSidebarOpen ? "w-full md:w-[42%]" : "w-0"
            } border-r border-gray-300 flex-shrink-0 h-full`}
          >
            {isSidebarOpen && (
              <div className="h-full overflow-y-auto scrollbar-thin">
                <DrawingControls
                onDrawingModeChange={setDrawingMode}
                drawingMode={drawingMode}
                onSaveTerritory={handleSaveTerritory}
                territories={territories}
                onOpenDeleteModal={handleOpenDeleteModal}
                onSelectTerritory={setSelectedTerritory}
                selectedTerritory={selectedTerritory}
                tempPolygon={tempPolygon}
                tempPolygonOverlap={tempPolygonOverlap}
                onCancelDrawing={handleCancelDrawing}
                editingTerritory={editingTerritory}
                onStartEdit={handleStartEdit}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                onToggleStatus={handleToggleStatus}
                isLoading={isLoading}
                isLoadingTerritories={isLoadingTerritories}
                showNameInput={showNameInput}
                setShowNameInput={setShowNameInput}
                territoryName={territoryName}
                setTerritoryName={setTerritoryName}
                territoryComment={territoryComment}
                setTerritoryComment={setTerritoryComment}
                selectedSalesOfficeIds={selectedSalesOfficeIds}
                setSelectedSalesOfficeIds={setSelectedSalesOfficeIds}
                totalCount={totalCount}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                onPageChange={handlePageChange}
                selectedSalesOfficeFilter={selectedSalesOfficeFilter}
                setSelectedSalesOfficeFilter={setSelectedSalesOfficeFilter}
              />
              </div>
            )}
          </div>

          {/* Map */}
          <div
            className={`relative transition-all duration-500 ease-in-out overflow-hidden ${
              isSidebarOpen ? "w-full md:w-[65%]" : "w-full"
            } h-full`}
          >
            {/* Minimize / maximize button (desktop) */}
            <div className="hidden md:block">
              <button
                onClick={toggleSidebar}
                className={`absolute top-1/2 z-20 px-1 py-6 bg-[#D099D4] text-white text-sm font-semibold transform -translate-y-1/2 transition-all duration-300 ease-in-out hover:scale-105 focus:outline-none flex items-center space-x-2 ${
                  isSidebarOpen ? "left-15px" : "left-0"
                }`}
              >
                <span>{isSidebarOpen ? "<" : ">"}</span>
              </button>
            </div>

            <div className="h-full p-4 overflow-hidden relative">
              {/* Eraser Button - Visible when drawing or when polygon is completed */}
              {(drawingMode !== null || tempPolygon !== null || isCancelling) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Call the cancel function to ensure everything is cleared properly
                    handleCancelDrawing();
                  }}
                  className="absolute top-6 right-6 z-30 w-12 h-12 rounded-full bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                  title="Cancel Drawing"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21" />
                    <path d="M22 21H7" />
                    <path d="m5.1 11.9 11.8 11.8" />
                  </svg>
                </button>
              )}
              <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAP_KEY || ""}>
                <Map
                  style={{ width: "100%", height: "100%" }}
                  defaultCenter={saudiCenter}
                  defaultZoom={7}
                  gestureHandling={"greedy"}
                  disableDefaultUI={true}
                  minZoom={6}
                  maxZoom={18}
                  zoomControl={true}
                  restriction={{
                    latLngBounds: saudiBounds,
                    strictBounds: true,
                  }}
                >
                <MapClickHandler
                  onMapClick={() => {
                    if (!editingTerritory) {
                      setSelectedTerritory(null);
                    }
                  }}
                />
                <MapDrawingManager
                  ref={drawingManagerCancelRef}
                  drawingMode={drawingMode}
                  onPolygonComplete={handlePolygonComplete}
                />
                <TerritoryPolygons
                  territories={territories}
                  selectedTerritory={selectedTerritory}
                  tempPolygon={tempPolygon}
                  tempPolygonOverlap={tempPolygonOverlap}
                  onTerritoryClick={(id) =>
                    setSelectedTerritory(id === selectedTerritory ? null : id)
                  }
                  editingTerritory={editingTerritory}
                  onTerritoryUpdate={handleTerritoryUpdate}
                  editingCoordinates={editingCoordinates}
                />
              </Map>
            </APIProvider>
            </div>
          </div>
        </div>
      </div>
    </DashboardPage>
  );
};

export default TerritoryMapView;

